import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { Order } from '@/types/sorting'
import type { DeepCamelCase } from '@/types/utils'
import { getOrdering } from '@/utils/get-ordering'
import { roomType } from '@/utils/zod'

import type { RoomType } from '../constants/room-types'

type APIResponse = z.infer<typeof responseSchema>
export type Room = DeepCamelCase<APIResponse>[number]

const responseSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    capacity: z.number().nullable(),
    room_type: roomType,
    can_be_deleted: z.boolean()
  })
)

const parseResponse = (response: z.infer<typeof responseSchema>): Room[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    capacity: item.capacity,
    roomType: item.room_type,
    canBeDeleted: item.can_be_deleted
  }))

type Params = {
  id?: string[]
  page?: number
  pageSize?: number
  type?: RoomType[]
  sort?: string
  order?: Order
}

export const useRooms = (params: Params) =>
  useQuery({
    queryKey: ['panelRoomsListPaginated', params],
    queryFn: async () => {
      const response = await httpService.panel.panelRoomsList({
        id: params.id,
        page: params.page,
        page_size: params.pageSize,
        room_type: params.type,
        ordering: getOrdering(params),
        fetchKeys: {
          id: true,
          name: true,
          capacity: true,
          room_type: true,
          can_be_deleted: true
        }
      })
      const parsedRooms = parseResponse(responseSchema.parse(response.results))

      return {
        list: parsedRooms,
        count: response.count
      }
    }
  })
